import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  type AuthResponse,
  type User,
  authApi,
  type LoginRequest,
  type RegisterRequest,
} from "./api";

// Interface for our Auth Store
interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  isLoading: boolean;

  // Actions
  login: (data: LoginRequest) => Promise<AuthResponse>;
  register: (data: RegisterRequest) => Promise<AuthResponse>;
  logout: () => void;
  refreshUser: () => Promise<User | null>;
  setAuth: (auth: AuthResponse) => void;
  validateAuth: () => Promise<boolean>;
  setUser: (user: User) => void;
}

/**
 * Validates the authentication token and ensures it's still valid
 * @returns Promise<boolean> True if token is valid, false otherwise
 */
// const validateToken = async (): Promise<boolean> => {
//   try {
//     const token = localStorage.getItem("auth_token");
//     if (!token) {
//       console.warn("No token found in localStorage");
//       return false;
//     }

//     console.log("Validating token:", token);

//     // Try to fetch user data as a validation check
//     const user = await authApi.getMe();
//     console.log("Token validation successful, user:", user);

//     // Update auth store with validated user data
//     const store = useAuthStore.getState();
//     store.setAuth({ token, user });

//     return true;
//   } catch (error: any) {
//     console.warn("Token validation failed:", error);

//     // Clear invalid token
//     localStorage.removeItem("auth_token");

//     // If we get a 401 or any other error, consider the token invalid
//     return false;
//   }
// };

// Create Auth Store with Zustand
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      user: null,
      token: null,
      isLoading: false,

      // Validate token and set auth state accordingly
      validateAuth: async () => {
        try {
          // Check if we have a token in state or localStorage
          const token = get().token || localStorage.getItem("auth_token");
          if (!token) {
            console.warn("No token found in state or localStorage");
            get().logout();
            return false;
          }

          // Try to fetch user data as a validation check
          const user = await authApi.getMe();

          // Update auth state with validated token and user
          set({
            isAuthenticated: true,
            user,
            token,
            isLoading: false,
          });

          return true;
        } catch (error: any) {
          console.error("Auth validation failed:", error);

          // Clear invalid token
          if (error?.status === 401 || error?.unauthorized) {
            console.warn("Token is invalid or expired, logging out");
            get().logout();
          }

          return false;
        }
      },

      // Login action
      login: async (data) => {
        try {
          set({ isLoading: true });
          const response = await authApi.login(data);
          get().setAuth(response);
          return response;
        } catch (error) {
          console.error("Login failed:", error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Register action
      register: async (data) => {
        try {
          set({ isLoading: true });
          const response = await authApi.register(data);
          get().setAuth(response);
          return response;
        } catch (error) {
          console.error("Registration failed:", error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Logout action
      logout: () => {
        localStorage.removeItem("auth_token");
        set({
          isAuthenticated: false,
          user: null,
          token: null,
          isLoading: false,
        });
      },

      // Enhanced refreshUser with better error handling
      refreshUser: async () => {
        try {
          set({ isLoading: true });
          const token = get().token || localStorage.getItem("auth_token");

          if (!token) {
            console.warn("No token available for refresh");
            get().logout();
            return null;
          }
          console.log("Refreshing user");
          const user = await authApi.getMe({ refresh: true });

          set({
            user,
            isAuthenticated: true,
            token,
            isLoading: false,
          });
          return user;
        } catch (error: any) {
          console.error("Failed to refresh user:", error);

          // Handle unauthorized errors (401) or explicit unauthorized flag
          if (error?.status === 401 || error?.unauthorized) {
            console.warn("Token is invalid or expired during refresh");
            get().logout();
          }

          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Set authentication state
      setAuth: (auth: AuthResponse) => {
        localStorage.setItem("auth_token", auth.token);
        set({
          isAuthenticated: true,
          user: auth.user,
          token: auth.token,
          isLoading: false,
        });
      },

      // Set user
      setUser: (user: User) => {
        set({
          user,
          isAuthenticated: true,
          token: localStorage.getItem("auth_token"),
          isLoading: false,
        });
      },
    }),
    {
      name: "auth-storage",
      // Store token, user and authentication state for persistence
      partialize: (state) => ({
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        isLoading: state.isLoading,
      }),
    },
  ),
);

// Add storage event listener to handle token changes
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === "auth_token") {
      const token = event.newValue;
      const store = useAuthStore.getState();

      if (token) {
        // Update store with new token
        store.setAuth({ token, user: store.user! });
      } else {
        // Token was removed, logout
        store.logout();
      }
    }
  });
}

// Function to check if the user is *potentially* authenticated based on token presence.
// The actual validation happens in validateAuth/refreshUser.
export const isAuthenticated = () => {
  // Check for token in Zustand state first
  const tokenInState = useAuthStore.getState().token;
  if (tokenInState) {
    return true; // If token is in state, assume potentially authenticated
  }

  // As a fallback, check localStorage directly
  const tokenInStorage = localStorage.getItem("auth_token");
  if (tokenInStorage) {
    // If token is in storage but not state, it might be initializing.
    // Let validateAuth/refreshUser handle the actual validation.
    return true; // Assume potentially authenticated
  }

  // No token found anywhere
  return false;
};

// Auth hook to get current user
export const useAuth = () => {
  const { isAuthenticated, user, login, register, logout, refreshUser } =
    useAuthStore();
  return { isAuthenticated, user, login, register, logout, refreshUser };
};
